import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { getProducts, getCategories } from "../lib/api";
import ProductCard from "../components/ProductCard";
import GoldDivider from "../components/GoldDivider";
import Reveal from "../components/Reveal";

const SORTS = [
  { value: "popularity", label: "Popularité" },
  { value: "newest", label: "Nouveautés" },
  { value: "price-asc", label: "Prix croissant" },
  { value: "price-desc", label: "Prix décroissant" },
];

const PRICE_RANGES = [
  { label: "Tous les prix", min: undefined, max: undefined },
  { label: "Moins de 10 000 FCFA", min: undefined, max: 10000 },
  { label: "10 000 – 20 000 FCFA", min: 10000, max: 20000 },
  { label: "20 000 – 30 000 FCFA", min: 20000, max: 30000 },
  { label: "Plus de 30 000 FCFA", min: 30000, max: undefined },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = searchParams.get("category") || "all";
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "popularity";
  const featured = searchParams.get("featured") || "";
  const priceIdx = parseInt(searchParams.get("price") || "0", 10);

  useEffect(() => {
    getCategories().then(setCategories);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const range = PRICE_RANGES[priceIdx] || PRICE_RANGES[0];
    getProducts({
      category,
      search: search || undefined,
      sort,
      featured: featured || undefined,
      min_price: range.min,
      max_price: range.max,
    })
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [category, search, sort, featured, priceIdx]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === "" || value === "all" || value === "0") next.delete(key);
    else next.set(key, value);
    if (key === "category" || key === "featured") next.delete("search");
    setSearchParams(next);
  };

  const activeTitle = search
    ? `Résultats pour « ${search} »`
    : featured === "bestseller"
    ? "Meilleures ventes"
    : category === "nouveautes"
    ? "Nouveautés"
    : category !== "all"
    ? categories.find((c) => c.slug === category)?.name || "Boutique"
    : "Tous nos produits";

  const FilterPanel = () => (
    <div className="space-y-8">
      <div>
        <h4 className="font-display text-xl text-[#0A0A0A] mb-4">Catégories</h4>
        <ul className="space-y-2">
          <li>
            <button
              onClick={() => { update("category", "all"); update("featured", ""); }}
              className={`text-left text-sm w-full py-1 transition ${category === "all" && !featured ? "text-[#B8860B] font-medium" : "text-neutral-600 hover:text-[#B8860B]"}`}
              data-testid="filter-cat-all"
            >
              Tous les produits
            </button>
          </li>
          {categories.map((c) => (
            <li key={c.slug}>
              <button
                onClick={() => { update("category", c.slug); update("featured", ""); }}
                className={`text-left text-sm w-full py-1 transition ${category === c.slug ? "text-[#B8860B] font-medium" : "text-neutral-600 hover:text-[#B8860B]"}`}
                data-testid={`filter-cat-${c.slug}`}
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <GoldDivider />

      <div>
        <h4 className="font-display text-xl text-[#0A0A0A] mb-4">Prix</h4>
        <ul className="space-y-2">
          {PRICE_RANGES.map((r, i) => (
            <li key={i}>
              <button
                onClick={() => update("price", String(i))}
                className={`text-left text-sm w-full py-1 transition ${priceIdx === i ? "text-[#B8860B] font-medium" : "text-neutral-600 hover:text-[#B8860B]"}`}
                data-testid={`filter-price-${i}`}
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <div data-testid="shop-page">
      {/* Header band */}
      <section className="hero-noir py-16 px-4 md:px-8 text-center">
        <span className="text-xs uppercase tracking-[0.3em] text-[#B8860B]">Boutique MaxiChoix</span>
        <h1 className="font-display text-4xl md:text-6xl text-white mt-3 mb-4">{activeTitle}</h1>
        <GoldDivider />
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block">
          <FilterPanel />
        </aside>

        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setFiltersOpen(true)}
              className="lg:hidden btn-outline-gold px-4 py-2 rounded-sm text-sm"
              data-testid="open-filters"
            >
              <SlidersHorizontal size={16} /> Filtres
            </button>
            <p className="text-sm text-neutral-500 hidden lg:block">{products.length} produit(s)</p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500 hidden sm:inline">Trier par</span>
              <select
                value={sort}
                onChange={(e) => update("sort", e.target.value)}
                className="border border-neutral-200 rounded-sm py-2 px-3 text-sm focus:outline-none focus:border-[#B8860B]"
                data-testid="sort-select"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-neutral-100 animate-pulse rounded" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display text-2xl text-[#0A0A0A] mb-2">Aucun produit trouvé</p>
              <p className="text-neutral-500">Essayez de modifier vos filtres.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {products.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.06}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filters drawer */}
      {filtersOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="filters-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-sm bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl">Filtres</h3>
              <button onClick={() => setFiltersOpen(false)}><X size={24} /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setFiltersOpen(false)} className="btn-gold w-full py-3 rounded-sm mt-8">
              Voir les résultats
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
